precision highp float;

uniform sampler2D uTexture;
uniform float uTime;
uniform float uScrollOffset;
uniform float uOpacity;
uniform float uMovementStrength;
uniform float uHover;
uniform vec2 uMousePos;
uniform float uIsMobile;
uniform float uIsVideo;
uniform float uEntryProgress;

varying vec2 vUv;
varying float vDepth;

float safeDiv(float a, float b) {
  return a / max(abs(b), 0.000001);
}

float sdLine(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float denom = max(dot(ba, ba), 0.000001);
  float h = clamp(dot(pa, ba) / denom, 0.0, 1.0);
  return length(pa - ba * h);
}

float drawArrow(vec2 p) {
  vec2 tip = vec2(0.0, 0.0);
  vec2 tail = vec2(-0.06, 0.0);
  vec2 wing1 = vec2(-0.02, 0.02);
  vec2 wing2 = vec2(-0.02, -0.02);

  float d = sdLine(p, tail, tip);
  d = min(d, sdLine(p, tip, wing1));
  d = min(d, sdLine(p, tip, wing2));

  return d;
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);

  return mix(
    mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  mat2 m = mat2(1.6, 1.2, -1.2, 1.6);

  for (int i = 0; i < 3; i++) {
    v += a * noise(p);
    p = m * p * 1.9;
    a *= 0.5;
  }

  return v;
}

void main() {
  vec2 uv = vUv;
  vec2 distFromCenter = abs(vUv - 0.5) * 2.0;
  float combinedDepth = max(distFromCenter.x, distFromCenter.y);

  vec4 tex = texture2D(uTexture, uv);
  if (uIsVideo > 0.5) {
      tex.rgb = pow(tex.rgb, vec3(1.8));
  }
  
  vec2 centeredUv = uv - vec2(0.5);
  vec2 p = centeredUv - uMousePos;

  vec2 dirToCenter = -uMousePos;
  float angle = atan(dirToCenter.y, dirToCenter.x);

  float cosA = cos(angle);
  float sinA = sin(angle);

  vec2 rotatedP = vec2(
    p.x * cosA + p.y * sinA,
   -p.x * sinA + p.y * cosA
  );

  float arrowDist = drawArrow(rotatedP);
  float lineThickness = 0.003;
  float blurEdge = 0.002;

  float arrowStrength = 1.0 - smoothstep(lineThickness, lineThickness + blurEdge, arrowDist);
  arrowStrength = clamp(arrowStrength, 0.0, 1.0);

  float distMouseToCenter = length(uMousePos);
  arrowStrength *= smoothstep(0.02, 0.05, distMouseToCenter);

  float hover = clamp(uHover, 0.0, 1.0);
  float mobile = clamp(uIsMobile, 0.0, 1.0);

  float pulse = 0.7 + 0.3 * sin(uTime * 3.0);
  vec3 arrowColor = vec3(1.0) * arrowStrength * pulse * hover * (1.0 - mobile);

#ifdef CLEAN_MODE
  gl_FragColor = vec4(clamp(tex.rgb + arrowColor, 0.0, 1.0), clamp(tex.a * uOpacity, 0.0, 1.0));
  return;
#endif

  if (combinedDepth < 0.1 && uEntryProgress > 0.99) {
    gl_FragColor = vec4(clamp(tex.rgb + arrowColor, 0.0, 1.0), clamp(tex.a * uOpacity, 0.0, 1.0));
    return;
  }

  float t = uTime * 0.4;
  float baseIntensity = 1.0;
  float frayThresholdBase = 0.6;

  vec2 st = vUv * 3.0;
  st.y += uScrollOffset * 0.2;

  float baseNoise = fbm(st + vec2(0.0, t * 0.2));
  vec2 r = st + vec2(baseNoise) * 1.2 - vec2(0.0, t * 0.4);

  float motion = 0.35;
  float anchor = smoothstep(0.0, motion, combinedDepth);

  float smokeNoise = 0.0;
  float totalW = 0.0;
  const int STEPS = 3;

  for (int i = 0; i < STEPS; i++) {
    float fi = float(i) / float(STEPS - 1);
    float w = 1.0 - fi;
    vec2 sampleR = r - motion * fi * 2.5 * anchor;
    smokeNoise += fbm(sampleR) * w;
    totalW += w;
  }

  smokeNoise /= max(totalW, 0.000001);

  float edgeBoost = 0.3;
  float d = (combinedDepth * (baseIntensity + uMovementStrength + edgeBoost)) + (smokeNoise - 0.5) * 1.4;

  float entryInv = 1.0 - clamp(uEntryProgress, 0.0, 1.0);
  d += entryInv * 3.5; 

  float cutLimit = mix(frayThresholdBase, 0.3, uMovementStrength);
  
  float entrySmooth = mix(0.4, 0.15, clamp(uEntryProgress, 0.0, 1.0));
  float frayMask = 1.0 - smoothstep(cutLimit, cutLimit + entrySmooth, d);

  float smokePresence = smoothstep(0.2, 0.5, d);

  vec3 smokeColor = mix(
    vec3(0.05, 0.05, 0.07),
    vec3(0.4, 0.42, 0.45),
    smokeNoise
  );

  vec3 mixedColor = mix(tex.rgb, smokeColor, smokePresence * combinedDepth);
  mixedColor += arrowColor;

  float alpha = clamp(tex.a * frayMask * uOpacity, 0.0, 1.0);

  gl_FragColor = vec4(clamp(mixedColor, 0.0, 1.0), alpha);
  gl_FragColor.a *= step(0.005, gl_FragColor.a);
}